import { ChakraAvatar } from '@equip.health/ui';
import { CarePlanNoteStatusHistoryResponse } from '~/lib/types/carePlan/carePlans';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import { useGetProviderInfoWithHistoryByIdQuery } from '~/store/services/providers';
import PatientNoteGoalStatusHistoryTooltipContent from './PatientNoteGoalStatusHistoryTooltipContent';

type PatientNoteGoalStatusProviderAvatarProps = {
  statusHistory?: CarePlanNoteStatusHistoryResponse;
  goalId: string;
  noteExternalId: string;
  description?: string | null;
};

/**
 * Displays a provider avatar for the most recent status change in a goal.  The avatar is suppressed when the latest status change was authored inside the current draft note.
 */
export const PatientNoteGoalStatusProviderAvatar = ({
  statusHistory,
  goalId,
  noteExternalId,
  description,
}: PatientNoteGoalStatusProviderAvatarProps) => {
  const providerExternalId = statusHistory?.updatedByUserId;

  const { data: providerData, isLoading } =
    useGetProviderInfoWithHistoryByIdQuery(
      {
        includeAvatar: true,
        includeOutOfOffice: true,
        includeProviderHistory: false,
        providerExternalId: providerExternalId || '',
      },
      {
        skip:
          !statusHistory ||
          !providerExternalId ||
          statusHistory.noteExternalId === noteExternalId,
      },
    );

  if (
    !statusHistory ||
    !providerExternalId ||
    statusHistory.noteExternalId === noteExternalId ||
    (!providerData && !isLoading)
  ) {
    return null;
  }

  const avatarName =
    providerData?.firstName && providerData?.lastName
      ? `${providerData.firstName} ${providerData.lastName}`
      : undefined;

  return (
    <ProviderAvatarTooltipWrapper
      placement="top"
      providerExternalId={providerExternalId}
      tooltipLabel={
        <PatientNoteGoalStatusHistoryTooltipContent
          goalId={goalId}
          providerExternalId={providerExternalId}
          statusHistory={statusHistory}
          description={description}
        />
      }
    >
      <ChakraAvatar
        name={avatarName}
        size="xs"
        src={providerData?.avatar?.thumbnailUrl}
      />
    </ProviderAvatarTooltipWrapper>
  );
};
